import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  CellControllerKind,
  InputNumberControllerEntity,
} from 'entities/TableControllers';
import { ILocalStore } from 'stores/interfaces';
import { debounce } from 'utils/debounce';
import { DENORMALIZE_NUMBER, formatNumberControllerValue } from 'utils/number';
import { getDecimalSeparator, normalizeNumberForServer } from 'utils/number/locale';

import { ITableControllerBase, TableControllerParams } from '../interface';

type PrivateFields = '_value';

const INPUT_CHANGE_DEBOUNCE_TIME = 500;


export default class TableInputNumberController
  implements ITableControllerBase<string>, ILocalStore
{
  constructor({
    record,
    onSave,
  }: TableControllerParams<InputNumberControllerEntity>) {
    const stringValue = record.controller.value !== null
      ? String(record.controller.value).replace('.', getDecimalSeparator())
      : '';

    this._value = {
      ...record.controller,
      value: stringValue,
    };
    this._initialValue = record.controller;

    makeObservable<TableInputNumberController, PrivateFields>(this, {
      _value: observable.ref,
      controllerValue: computed,
      formattedDisplayValue: computed,
      setValue: action.bound,

      toServerFormat: computed,

      reset: action.bound,
    });

    const onSaveHandler = debounce(async () => {
      await onSave();
      this._isUpdateBlocked = false;
    }, INPUT_CHANGE_DEBOUNCE_TIME);

    this._reactions.push(
      reaction(
        () => this._value,
        async () => {
          this._isUpdateBlocked = true;
          await onSaveHandler();
        }
      )
    );
  }

  private _value: Omit<InputNumberControllerEntity['controller'], 'value'> & {
    value: string;
  };

  private readonly _reactions: IReactionDisposer[] = [];

  private readonly _initialValue: InputNumberControllerEntity['controller'];

  private _isUpdateBlocked = false;

  get isUpdateBlocked(): boolean {
    return this._isUpdateBlocked;
  }

  get controllerKind() {
    return CellControllerKind.INPUT_STRING;
  }

  get controllerValue(): string {
    return String(this._value.value);
  }

  get formattedDisplayValue(): string {
    if (this._value.value === '' || this._value.value === '-') {
      return this._value.value;
    }
    const normalized = normalizeNumberForServer(this._value.value);
    const num = Number(normalized);
    if (isNaN(num)) {
      return this._value.value;
    }
    return formatNumberControllerValue({ value: num, format: this._value.format });
  }

  get toServerFormat() {
    if (this._value.value === '') {
      return null;
    }
    const normalized = normalizeNumberForServer(this._value.value);
    const numValue = Number(normalized);
    return DENORMALIZE_NUMBER[this._value.format](numValue);
  }

  reset(): void {
    this._value = {
      value: '',
      format: this._initialValue.format,
    };
  }

  setValue(nextValue: string | number | null): void {
    let stringValue: string;
    if (nextValue === null) {
      stringValue = '';
    } else if (typeof nextValue === 'number') {
      stringValue = String(nextValue).replace('.', getDecimalSeparator());
    } else {
      stringValue = nextValue;
    }

    if (this._value.value === stringValue) {
      return;
    }

    this._value = {
      ...this._value,
      value: stringValue,
    };
  }

  destroy() {
    this._reactions.forEach((reaction) => reaction());
  }
}
